import type { SessionResponse } from "@/features/auth/auth.types";
import type { Workspace, WorkspaceListResponse } from "./workspace.types";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  type PropsWithChildren,
} from "react";
import { useNavigate } from "react-router-dom";

import { authKeys, useAuth } from "@/features/auth/AuthProvider";
import { apiRequest } from "@/shared/api/client";

export const workspaceKeys = { list: ["workspaces"] as const };

type WorkspaceContextValue = {
  workspaces: Workspace[];
  activeWorkspace: Workspace | null;
  isLoading: boolean;
  isSwitching: boolean;
  switchWorkspace: (tenantId: string) => Promise<void>;
};

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);

export function WorkspaceProvider({ children }: PropsWithChildren) {
  const { session } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const enabled = session?.isAuthenticated === true;
  const workspacesQuery = useQuery({
    queryKey: workspaceKeys.list,
    queryFn: () => apiRequest<WorkspaceListResponse>("/api/me/tenants"),
    enabled,
    retry: false,
    staleTime: 30_000,
  });
  const mutation = useMutation({
    mutationFn: (tenantId: string) =>
      apiRequest<SessionResponse>("/auth/session/active-tenant", {
        method: "PUT",
        body: JSON.stringify({ tenantId }),
      }),
    onSuccess: async (nextSession) => {
      queryClient.setQueryData(authKeys.session, nextSession);
      queryClient.setQueryData<WorkspaceListResponse>(
        workspaceKeys.list,
        (current) =>
          current
            ? {
                ...current,
                activeTenantId: nextSession.activeTenant!.id,
                data: current.data.map((workspace) => ({
                  ...workspace,
                  isActive: workspace.id === nextSession.activeTenant!.id,
                })),
              }
            : current,
      );
      await queryClient.removeQueries({ queryKey: ["tenant"] });
      navigate(`/app/${nextSession.activeTenant!.id}`);
    },
  });

  const switchWorkspace = useCallback(
    async (tenantId: string) => {
      if (
        tenantId === workspacesQuery.data?.activeTenantId ||
        mutation.isPending
      )
        return;
      await mutation.mutateAsync(tenantId);
    },
    [mutation, workspacesQuery.data?.activeTenantId],
  );

  const workspaces = workspacesQuery.data?.data ?? [];
  const activeWorkspace =
    workspaces.find(({ id }) => id === workspacesQuery.data?.activeTenantId) ??
    null;
  const value = useMemo(
    () => ({
      workspaces,
      activeWorkspace,
      isLoading: enabled && workspacesQuery.isLoading,
      isSwitching: mutation.isPending,
      switchWorkspace,
    }),
    [
      activeWorkspace,
      enabled,
      mutation.isPending,
      switchWorkspace,
      workspaces,
      workspacesQuery.isLoading,
    ],
  );

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
}

export function useWorkspaces() {
  const context = useContext(WorkspaceContext);

  if (!context)
    throw new Error("useWorkspaces must be used within WorkspaceProvider");

  return context;
}
